/**
 * 品牌配置单一数据源。
 *
 * 通过构建期 env 变量注入，供白标 / 多客户构建定制品牌名、浏览器标题与 Logo：
 *  - REACT_APP_DEPLOY_NAME     部署客户标识（velax），唯一的客户定制功能开关依据；
 *                              由 .env.production / .env.test 提供
 *  - REACT_APP_BRAND_NAME      应用内品牌名，覆盖 i18n common.appName（留空回退 i18n），
 *                              纯展示用，不参与任何功能判定
 *  - REACT_APP_BRAND_LOGO_URL  品牌 Logo 图片 URL（留空回退内置 SVG）
 *
 * env 变量在构建期由 CRA 内联，运行时为静态值，因此这里读取一次即可。
 */

const rawDeployName = (process.env.REACT_APP_DEPLOY_NAME ?? '').trim().toLowerCase()
const rawBrandName = (process.env.REACT_APP_BRAND_NAME ?? '').trim()
const rawLogoUrl = (process.env.REACT_APP_BRAND_LOGO_URL ?? '').trim()

export const brandConfig = {
  /** 部署客户标识（已小写化）；空串表示未配置，所有客户定制功能关闭。 */
  deployName: rawDeployName,
  /** env 配置的品牌名；空串表示未配置，调用方应回退到 i18n 默认值。 */
  name: rawBrandName,
  /** env 配置的 Logo 图片 URL；空串表示未配置，渲染内置 SVG。 */
  logoUrl: rawLogoUrl,
} as const

/**
 * velax 客户定制功能开关（issue #52）。
 *
 * 判定：REACT_APP_DEPLOY_NAME 去空格、忽略大小写等于 'velax' 时开启，其他值或留空关闭。
 * 不再看 REACT_APP_BRAND_NAME——品牌名是给客户改展示文案的，同一客户可能随时改名。
 * env 由 CRA 构建期内联，以下开关对每个客户构建是编译期常量，关闭分支会被打包器
 * 当作死代码剔除——非 velax 构建产物不含定制功能代码。
 *
 * 客户标识字符串比较只允许出现在本文件；页面一律消费下面的语义化开关。
 */
const isVelaxDeploy = rawDeployName === 'velax'

export const features = {
  /** APP 域名管理页（/system/app-domain）菜单与路由入口 */
  appDomainMgmt: isVelaxDeploy,
  /** 用户列表注册来源域名列与搜索项 */
  registrationDomain: isVelaxDeploy,
  /** KYC 姓名/证件号明文优先读取；关闭时优先 *_masked 脱敏字段 */
  kycPlaintext: isVelaxDeploy,
  /** 手机号展示与录入（KYC 列表列/详情行/代认证表单、用户列表列/详情行） */
  kycPhone: isVelaxDeploy,
  /** 管理员删除与重置密码入口 */
  adminAccountOps: isVelaxDeploy,
  /**
   * 现货管理（Epic #78）：系统设置的 spot / product_lines / modules 分组与枚举下拉（#81）、
   * 交易对现货开关列（#80）、现货订单页 /trading/spot-orders（#79）。
   * 对应 velax-api 的现货接口与配置分组，仅 velax 开启。
   */
  spotAdmin: isVelaxDeploy,
  /**
   * 跟单增量管理（Epic #78）：跟随关系 / 跟单持仓 / 执行日志只读视图（#82）与交易员批量生成（#83）。
   * 对应 velax-api 新增的 copy-trade Admin 接口，仅 velax 开启。
   */
  copyTradeAdminExt: isVelaxDeploy,
  /**
   * 业务数据按 APP 域名区分（velax-api D-1961）：右上角域名切换、请求头 X-App-Domain-Id、
   * 各业务列表的所属域名列与新增 / 编辑的所属域名选择、交易对上架域名，仅 velax 开启。
   */
  appDomainPartition: isVelaxDeploy,
  /**
   * 二元期权配置的新增 / 删除，以及周期「是否需要单独授权」的展示与编辑。
   * 对应 velax-api 新增的接口与字段，仅 velax 开启。
   */
  binaryOptionsConfigCrud: isVelaxDeploy,
  /**
   * 期权订单的平局规则（velax-api D-2016）：订单列表列 / 筛选与详情展示下单时定格的「平局算输 / 平局退还」。
   * 仅 velax 开启。
   */
  binaryTieRule: isVelaxDeploy,
  /**
   * 用户列表「账户总余额（USDT）」列（velax-api D-2041）：资金 + 理财 + 量化折 USDT 合计。
   * 仅 velax 开启。
   */
  userTotalBalance: isVelaxDeploy,
} as const

/**
 * 取生效的品牌名：env 优先，未配置时回退到传入的 i18n 默认值。
 */
export function resolveBrandName(i18nFallback: string): string {
  return brandConfig.name || i18nFallback
}

/**
 * 由品牌名 + 后缀派生浏览器标题。后缀为空时仅返回品牌名。
 */
export function buildDocumentTitle(name: string, suffix: string): string {
  const trimmedSuffix = suffix.trim()
  return trimmedSuffix ? `${name} ${trimmedSuffix}` : name
}
